import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SparklesIcon, CloudArrowUpIcon, TrashIcon, ArrowsRightLeftIcon } from '@heroicons/react/24/outline';
import { AssetType, AssetGenerationStatus, IProjectAsset } from '@packages/global';
import MediaPreviewModal from '../../common/MediaPreviewModal';
import { projectAssetApi } from '../../../services/user/projectAsset.api';
import ConfirmDialog from '../../common/ConfirmDialog';
import AssetGenerateModalV2 from '../../projectAssets/AssetGenerateModalV2';
import ProjectAssetSelector from '../../projectAssets/ProjectAssetSelector';

interface ItemCardProps {
  name: string;
  imageUrl?: string;
  prompt?: string;
  description?: string;
  scriptId: string;
  type: 'prop' | 'scene';
  projectId?: string;
  episodeId?: string;
  artStyle?: string;  // 新增：艺术风格
  onUpdate?: () => void;
  /** 图片正在生成中，禁用所有操作 */
  imageGenerating?: boolean;
  /** 来自 ProjectAsset 的生成状态（优先级高于 imageGenerating） */
  assetGenerationStatus?: AssetGenerationStatus;
  /** 来自 ProjectAsset 的图片 URL（优先级高于 imageUrl） */
  assetImageUrl?: string;
  /** 关联的 ProjectAsset 对象（用于更新/删除操作，替代 name 匹配） */
  matchedAsset?: IProjectAsset;
}

export default function ItemCard({ name, imageUrl, prompt, description, type, projectId, episodeId, artStyle, onUpdate, imageGenerating, assetGenerationStatus, assetImageUrl, matchedAsset }: ItemCardProps) {
  const { t } = useTranslation(['script', 'common']);
  const [showImageGenDialog, setShowImageGenDialog] = useState(false);
  const [showImageGenMode, setShowImageGenMode] = useState<'text' | 'image'>('text');
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showReplaceSelector, setShowReplaceSelector] = useState(false);

  // 优先使用 ProjectAsset 的图片 URL，其次使用脚本 JSON 字段
  const displayImageUrl = assetImageUrl || imageUrl;

  // 单个 asset 的生成状态（来自 ProjectAsset）
  const isAssetGenerating =
    assetGenerationStatus === AssetGenerationStatus.PENDING ||
    assetGenerationStatus === AssetGenerationStatus.GENERATING;
  // 卡片 loading 只反映"自身"是否在生成
  const isLoadingImage = isAssetGenerating || updating;
  // 交互禁用：当前 asset 正在生成，或整个步骤在生成
  const isInteractionDisabled = isLoadingImage || imageGenerating;

  const handleReplaceFromAsset = async (asset: IProjectAsset, _value: string) => {
    if (!matchedAsset?.id) {
      alert(t('characters.replaceFailed'));
      return;
    }
    setUpdating(true);
    try {
      await projectAssetApi.updateProjectAsset(matchedAsset.id, {
        description: asset.description,
        prompt: asset.prompt || '',
        conceptImageUrl: asset.conceptImageUrl,
      });
      onUpdate?.();
      setShowReplaceSelector(false);
    } catch (error: any) {
      alert(error.message || t('characters.replaceFailed'));
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async () => {
    if (!matchedAsset?.id) {
      alert(t('itemCard.deleteFailed', { type: t(`itemCard.type.${type}`) }));
      return;
    }
    try {
      setUpdating(true);
      await projectAssetApi.deleteProjectAsset(matchedAsset.id);
      onUpdate?.();
      setShowDeleteConfirm(false);
    } catch (error: any) {
      console.error('Failed to delete item:', error);
      alert(error.message || t('itemCard.deleteFailed', { type: t(`itemCard.type.${type}`) }));
    } finally {
      setUpdating(false);
    }
  };

  const handleImageGeneration = async (result: { imageUrl: string; prompt: string; subjectDescription?: string }) => {
    if (!matchedAsset?.id) {
      alert(t('itemCard.updateImageFailed', { type: t(`itemCard.type.${type}`) }));
      return;
    }
    setUpdating(true);
    try {
      await projectAssetApi.updateProjectAsset(matchedAsset.id, {
        conceptImageUrl: result.imageUrl,
        prompt: result.prompt,
        // 同步保存弹窗中编辑后的描述，避免描述与图片/提示词不一致导致生成时使用旧描述
        ...(result.subjectDescription !== undefined ? { description: result.subjectDescription } : {}),
      });
      onUpdate?.();
    } catch (error: any) {
      console.error('Failed to update item image:', error);
      alert(error.message || t('itemCard.updateImageFailed', { type: t(`itemCard.type.${type}`) }));
    } finally {
      setUpdating(false);
    }
  };

  return (
    <>
      <div className="bg-bg-surface rounded-lg overflow-hidden group">
        <div className="relative w-full aspect-square">
          {isLoadingImage ? (
            <div className="w-full h-full bg-bg-elevated flex flex-col items-center justify-center gap-2">
              <div className="relative">
                <SparklesIcon className="w-8 h-8 text-blue-400 animate-pulse" />
                <div className="absolute inset-0 bg-blue-400/20 rounded-full animate-ping" />
              </div>
              {isAssetGenerating && !updating && (
                <span className="text-xs text-blue-300">{t('characters.imageGenerating')}</span>
              )}
            </div>
          ) : displayImageUrl ? (
            <>
              {imageLoading && (
                <div className="absolute inset-0 bg-bg-elevated flex items-center justify-center">
                  <div className="relative">
                    <SparklesIcon className="w-8 h-8 text-blue-400 animate-pulse" />
                    <div className="absolute inset-0 bg-blue-400/20 rounded-full animate-ping" />
                  </div>
                </div>
              )}
              <img
                src={displayImageUrl}
                alt={name}
                className="w-full h-full object-cover cursor-pointer hover:opacity-90 transition-opacity"
                onLoad={() => setImageLoading(false)}
                onError={() => setImageLoading(false)}
                onClick={() => setShowImagePreview(true)}
              />
            </>
          ) : (
            <div className="w-full h-full bg-bg-elevated flex items-center justify-center">
              <CloudArrowUpIcon className="w-10 h-10 text-gray-500" />
            </div>
          )}

          {!isLoadingImage && (
            <button
              onClick={() => setShowDeleteConfirm(true)}
              disabled={isInteractionDisabled}
              className="absolute top-2 right-2 p-1.5 bg-red-600/80 hover:bg-red-700 backdrop-blur-sm rounded-full text-white transition-colors opacity-0 group-hover:opacity-100 disabled:opacity-50 disabled:cursor-not-allowed"
              title={t('itemCard.deleteTitle', { type: t(`itemCard.type.${type}`) })}
            >
              <TrashIcon className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="p-3 space-y-2">
          <div className="text-sm font-semibold text-gray-50">{name}</div>
          {/* 操作按钮 - 匹配设计图样式 */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => { setShowImageGenMode('text'); setShowImageGenDialog(true); }}
              disabled={isInteractionDisabled}
              className="p-1.5 bg-bg-elevated hover:bg-bg-surface rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              title={t('characters.aiGenerateImage')}
            >
              <SparklesIcon className="w-3.5 h-3.5 text-primary" />
            </button>
            <button
              onClick={() => { setShowImageGenMode('image'); setShowImageGenDialog(true); }}
              disabled={isInteractionDisabled}
              className="p-1.5 bg-bg-elevated hover:bg-bg-surface rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              title={t('characters.uploadRefImage')}
            >
              <CloudArrowUpIcon className="w-3.5 h-3.5 text-gray-300" />
            </button>
            <button
              onClick={() => setShowReplaceSelector(true)}
              disabled={isInteractionDisabled}
              className="p-1.5 bg-bg-elevated hover:bg-bg-surface rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              title={t('characters.replaceFromLibrary')}
            >
              <ArrowsRightLeftIcon className="w-3.5 h-3.5 text-gray-300" />
            </button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title={t('itemCard.deleteTitle', { type: t(`itemCard.type.${type}`) })}
        message={t('itemCard.deleteConfirm', { type: t(`itemCard.type.${type}`), name })}
        confirmText={t('common:delete')}
        cancelText={t('common:cancel')}
        type="danger"
      />

      <AssetGenerateModalV2
        isOpen={showImageGenDialog}
        onClose={() => setShowImageGenDialog(false)}
        subjectType={type === 'prop' ? AssetType.PROP : AssetType.SCENE}
        subjectName={name}
        subjectDescription={description}
        initialPrompt={prompt}
        artStyle={artStyle}
        existingImageUrl={displayImageUrl}
        initialMode={showImageGenMode}
        onConfirm={handleImageGeneration}
      />

      <MediaPreviewModal
        isOpen={showImagePreview}
        onClose={() => setShowImagePreview(false)}
        type="image"
        src={displayImageUrl || ''}
        alt={name}
      />

      <ProjectAssetSelector
        isOpen={showReplaceSelector}
        projectId={projectId}
        onSelect={handleReplaceFromAsset}
        onClose={() => setShowReplaceSelector(false)}
        initialAspect="image"
        initialAssetType={type === 'prop' ? AssetType.PROP : AssetType.SCENE}
      />
    </>
  );
}
